import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import RegisterFieldView from "./register-field-view";
import { z } from "zod";
import { Form } from "@/app/global-components/form";
import RegisteSubmitView from "./register-submit-view";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { ROLE } from "@/types";
import useRegister from "@/hooks/use-register";
import { showToast } from "@/app/global-components/toaster";
import { useNavigate } from "react-router-dom";
import { routes } from "@/consts";
import { Box, Typography, useTheme, alpha } from "@mui/material";

interface RegisterFormViewProps {
  Roll: string;
}

const RegisterFormView: React.FC<RegisterFormViewProps> = ({
  Roll = ROLE.BUYER,
}) => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const theme = useTheme();
  const { register, isLoading, error } = useRegister();
  const navigate = useNavigate();

  const RegisterSchema = z.object({
    nickName: z
      .string()
      .min(1, t("authentication.validation.usernameRequired")),
    email: z.string().email(t("authentication.validation.invalidEmail")),
    password: z
      .string()
      .min(8, t("authentication.changePassword.validation.passwordLength")),
    role: z.string(),
  });

  const form = useForm<z.infer<typeof RegisterSchema>>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      email: "",
      nickName: "",
      password: "",
      role: Roll,
    },
  });

  const onSubmit = async (values: z.infer<typeof RegisterSchema>) => {
    try {
      const response = await register(values);
      sessionStorage.setItem("email", values.email);
      if (response.isSuccess) {
        showToast("verification sent to email successfully", "success");
        navigate(routes.confirm);
      } else {
        showToast(response.message, "error");
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Form {...form}>
      <Box
        component="form"
        onSubmit={form.handleSubmit(onSubmit)}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <RegisterFieldView
          name="nickName"
          label={t("authentication.fields.username")}
          placeholder={t("authentication.fields.username")}
          formControl={form.control}
        />

        <RegisterFieldView
          name="email"
          label={t("profile.fields.email")}
          placeholder={t("profile.fields.email")}
          inputType="email"
          formControl={form.control}
        />

        <RegisterFieldView
          name="password"
          label={t("authentication.fields.password")}
          placeholder={t("authentication.fields.password")}
          description={t("authentication.validation.passwordDesc1.title")}
          inputType="password"
          formControl={form.control}
        />

        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            bgcolor: alpha(theme.palette.info.main, 0.1),
            p: 1.5,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          }}
        >
          {t("authentication.validation.passwordDesc1.body")}
        </Typography>

        <RegisteSubmitView isLoading={isLoading} />

        {error && (
          <Box
            sx={{
              color: "error.main",
              fontSize: "0.875rem",
              textAlign: "center",
              mt: 1,
            }}
          >
            {error}
          </Box>
        )}
      </Box>
    </Form>
  );
};

export default RegisterFormView;
